import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import axios from "@/axiosConfig";
import { authEndpoints } from "@/endpoints";
import { PermissionCodes, PermissionGroup } from "@/types/permissions.types";

// API functions
const fetchPermissionGroups = async (): Promise<PermissionGroup[]> => {
    const response = await axios.get(authEndpoints.permissionGroups);
    return response.data;
};

const fetchPermissionCodes = async (): Promise<PermissionCodes> => {
    const response = await axios.get(authEndpoints.permissionCodes);
    return response.data;
};

const createPermissionGroup = async (group: Omit<PermissionGroup, "id">): Promise<PermissionGroup> => {
    const response = await axios.post(authEndpoints.createPermissionGroup, group);
    return response.data;
};

const deletePermissionGroup = async (id: string): Promise<void> => {
    await axios.delete(authEndpoints.deletePermissionGroup(id));
};

type UpdateResult = {
    success: boolean;
    data?: PermissionGroup;
    nameUpdateFailed?: boolean;
    error?: any;
};

const updatePermissionGroup = async (group: PermissionGroup): Promise<UpdateResult> => {
    try {
        // First, get the existing data of the group
        const currentGroupResponse = await axios.get(`${authEndpoints.permissionGroups}/${group.id}`);
        const currentGroup = currentGroupResponse.data;

        // Only update permissions and color
        const permissionsUpdatePayload = {
            permissions: group.permissions,
            color: group.color,
        };

        // Update permissions and color
        const response = await axios.patch(authEndpoints.updatePermissionGroup(group.id), permissionsUpdatePayload);

        // If the name has changed, update it as well
        if (group.name !== currentGroup.name) {
            const nameUpdatePayload = {
                name: group.name,
            };

            try {
                await axios.patch(authEndpoints.updatePermissionGroup(group.id), nameUpdatePayload);
            } catch (error) {
                console.error("Name update error:", error);
                return {
                    success: true,
                    data: response.data,
                    nameUpdateFailed: true,
                };
            }
        }

        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        console.error("Group update error:", error);
        return {
            success: false,
            error,
        };
    }
};

export const usePermissions = () => {
    const queryClient = useQueryClient();
    const [selectedGroup, setSelectedGroup] = useState<PermissionGroup | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [localChanges, setLocalChanges] = useState(false);
    const [workingGroup, setWorkingGroup] = useState<PermissionGroup | null>(null);
    const { t } = useTranslation();
    // Get permission groups
    const {
        data: permissionGroups = [],
        isLoading: isLoadingGroups,
        error: groupsError,
    } = useQuery(["permissionGroups"], fetchPermissionGroups);

    // Get permission codes
    const {
        data: permissionCodes,
        isLoading: isLoadingCodes,
        error: codesError,
    } = useQuery(["permissionCodes"], fetchPermissionCodes);

    // Create new group
    const createGroupMutation = useMutation(createPermissionGroup, {
        onSuccess: () => {
            queryClient.invalidateQueries(["permissionGroups"]);
            // Invalidate user permissions to reload sidebar items
            queryClient.invalidateQueries([authEndpoints.me]);
            // Success message will be shown in the page component
        },
        onError: (error) => {
            toast.error(t("error_creating_permission_group"));
            console.error("Create group error:", error);
        },
    });

    // Update group
    const updateGroupMutation = useMutation(updatePermissionGroup, {
        onSuccess: (result) => {
            queryClient.invalidateQueries(["permissionGroups"]);
            // Invalidate user permissions to reload sidebar items
            queryClient.invalidateQueries([authEndpoints.me]);
            // Success message will be shown in the page component

            if (result.success) {
                if (result.nameUpdateFailed) {
                    toast.warning(t("permission_group_partial_update_success"));
                } else {
                    toast.success(t("permission_group_update_success"));
                }
            }
        },
        onError: (error) => {
            toast.error(t("error_updating_permission_group"));
            console.error("Update group error:", error);
        },
    });

    // Delete group
    const deleteGroupMutation = useMutation(deletePermissionGroup, {
        onSuccess: () => {
            queryClient.invalidateQueries(["permissionGroups"]);
            // Invalidate user permissions to reload sidebar items
            queryClient.invalidateQueries([authEndpoints.me]);
            // Success message will be shown in the page component
        },
        onError: (error) => {
            toast.error(t("error_deleting_permission_group"));
            console.error("Delete group error:", error);
        },
    });

    // Filtered groups
    const filteredGroups = permissionGroups.filter((group) =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Show warning when changing selected group if there are unsaved changes
    const handleSelectGroup = (group: PermissionGroup) => {
        // If there are changes and switching to a different group
        if (localChanges && selectedGroup && selectedGroup.id !== group.id) {
            if (window.confirm(t("unsaved_changes_message"))) {
                setSelectedGroup(group);
                setWorkingGroup(group);
                setLocalChanges(false);
            }
        } else {
            setSelectedGroup(group);
            setWorkingGroup(group);
        }
    };

    // Handle local permission changes without API calls
    const handleLocalPermissionChange = (permission: string, checked: boolean) => {
        if (!workingGroup) return;

        const updatedPermissions = checked
            ? [...workingGroup.permissions, permission]
            : workingGroup.permissions.filter((p) => p !== permission);

        setWorkingGroup({
            ...workingGroup,
            permissions: updatedPermissions,
        });
        setLocalChanges(true);
    };

    // Save changes to the server
    const saveChanges = () => {
        if (!workingGroup || !localChanges) return;

        updateGroupMutation.mutate(workingGroup);
        setLocalChanges(false);
    };

    // Discard changes and revert to the original state
    const cancelChanges = () => {
        if (selectedGroup) {
            setWorkingGroup(selectedGroup);
        }
        setLocalChanges(false);
    };

    // Clear selected group (if deleted)
    useEffect(() => {
        if (selectedGroup && !permissionGroups.find((g) => g.id === selectedGroup.id)) {
            setSelectedGroup(null);
            setWorkingGroup(null);
            setLocalChanges(false);
        }
    }, [permissionGroups, selectedGroup]);

    // Initialize working group when selected group changes
    useEffect(() => {
        if (selectedGroup) {
            setWorkingGroup(selectedGroup);
        }
    }, [selectedGroup]);

    // Permission change
    const handlePermissionChange = (groupId: string, permission: string, checked: boolean) => {
        const group = permissionGroups.find((g) => g.id === groupId);
        if (!group) return;

        const updatedPermissions = checked
            ? [...group.permissions, permission]
            : group.permissions.filter((p) => p !== permission);

        const updatedGroup = {
            ...group,
            permissions: updatedPermissions,
        };

        updateGroupMutation.mutate(updatedGroup);

        // Also update locally
        if (selectedGroup?.id === groupId) {
            setSelectedGroup(updatedGroup);
        }
    };

    return {
        permissionGroups,
        permissionCodes,
        selectedGroup,
        workingGroup,
        setSelectedGroup: handleSelectGroup,
        searchTerm,
        setSearchTerm,
        filteredGroups,
        isLoading: isLoadingGroups || isLoadingCodes,
        error: groupsError || codesError,
        hasLocalChanges: localChanges,
        handleLocalPermissionChange,
        saveChanges,
        cancelChanges,
        handlePermissionChange,
        handleAddGroup: (group: Omit<PermissionGroup, "id">) => createGroupMutation.mutate(group),
        handleUpdateGroup: (group: PermissionGroup) => updateGroupMutation.mutate(group),
        handleDeleteGroup: (id: string) => deleteGroupMutation.mutate(id),
    };
};
